import { useNavigate } from "react-router-dom";
import { edgeColor, themedHex } from "@/lib/format";
import { useTheme } from "@/context/ThemeContext";

const NODES = {
  "GHA-Rehab": { x: 110, y: 80, label: "GHA-Rehab" },
  "GHBC-Rehab": { x: 110, y: 200, label: "GHBC-Rehab" },
  "TVA-Rehab": { x: 110, y: 320, label: "TVA-Rehab" },
  "Culpeper": { x: 110, y: 440, label: "Culpeper" },
  HH: { x: 560, y: 120, label: "HH" },
  Hospice: { x: 870, y: 270, label: "Hospice" },
  HC: { x: 600, y: 430, label: "HC" },
};

// lane -> perpendicular offset (bidirectional pairs curve opposite ways)
const OFFSETS = {
  "HH\u2192Hospice": 34, "Hospice\u2192HH": -34,
  "HH\u2192HC": 30, "HC\u2192HH": -30,
  "Hospice\u2192HC": 34, "HC\u2192Hospice": -34,
  "Rehab\u2192HH": 0, "Rehab\u2192Hospice": 0,
};

function quadPath(p1, p2, off) {
  const mx = (p1.x + p2.x) / 2;
  const my = (p1.y + p2.y) / 2;
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;
  const cx = mx + nx * off;
  const cy = my + ny * off;
  // shorten ends so edges don't overlap node circles
  const shorten = (from, to) => {
    const ddx = to.x - from.x, ddy = to.y - from.y;
    const l = Math.hypot(ddx, ddy) || 1;
    return { x: from.x + (ddx / l) * 34, y: from.y + (ddy / l) * 34 };
  };
  const s = shorten(p1, { x: cx, y: cy });
  const e = shorten(p2, { x: cx, y: cy });
  const label = { x: 0.25 * s.x + 0.5 * cx + 0.25 * e.x, y: 0.25 * s.y + 0.5 * cy + 0.25 * e.y };
  return { d: `M ${s.x} ${s.y} Q ${cx} ${cy} ${e.x} ${e.y}`, label };
}

const MARKERS = { red: "#EF4444", yellow: "#EAB308", green: "#06B6D4", stale: "#64748B", none: "#334155" };

export default function PipelineGraph({ pipeline = [], uncounted = [], onLaneClick }) {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const nodeLoad = {};
  Object.keys(NODES).forEach((n) => (nodeLoad[n] = 0));
  pipeline.forEach((l) => {
    if (nodeLoad[l.source] != null) nodeLoad[l.source] += l.open;
    if (nodeLoad[l.receiving] != null) nodeLoad[l.receiving] += l.open;
  });

  return (
    <div className="glass rounded-xl p-4 relative overflow-hidden" data-testid="pipeline-graph">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-head font-bold text-sm uppercase tracking-[0.18em] text-muted-foreground">Referral Pipeline</h2>
        <span className="text-[11px] font-mono text-muted-foreground">edge = open volume · color = worst aging</span>
      </div>

      <svg viewBox="0 0 1000 520" className="w-full" style={{ maxHeight: 440 }}>
        <defs>
          <filter id="edgeGlow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="3" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          {Object.entries(MARKERS).map(([k, c]) => (
            <marker key={k} id={`arrow-${k}`} markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L7,3 L0,6 Z" fill={c} />
            </marker>
          ))}
        </defs>

        {pipeline.map((l) => {
          const p1 = NODES[l.source];
          const p2 = NODES[l.receiving];
          if (!p1 || !p2) return null;
          const { d, label } = quadPath(p1, p2, OFFSETS[l.key] ?? 0);
          const color = themedHex(edgeColor(l.severity), theme);
          const w = Math.min(2 + l.open * 1.4, 8);
          const active = l.open > 0;
          return (
            <g key={l.key} style={{ cursor: "pointer" }} onClick={() => onLaneClick && onLaneClick(l.key)}>
              <path d={d} fill="none" stroke={color} strokeWidth={w} strokeOpacity={active ? 0.9 : 0.28}
                    filter={active ? "url(#edgeGlow)" : undefined}
                    className={active ? "flow-edge" : ""} markerEnd={`url(#arrow-${active ? l.severity : "none"})`} />
              {active && (
                <>
                  <circle cx={label.x} cy={label.y} r="11" fill="#0A0E17" stroke={color} strokeWidth="1.2" />
                  <text x={label.x} y={label.y + 3.5} textAnchor="middle" fontSize="11" fontFamily="JetBrains Mono, monospace" fill={color} fontWeight="600">{l.open}</text>
                </>
              )}
            </g>
          );
        })}

        {Object.entries(NODES).map(([key, n]) => (
          <g key={key}>
            <circle cx={n.x} cy={n.y} r="30" fill="#0F172A" stroke="#22D3EE" strokeWidth="1.4" strokeOpacity="0.55" />
            <circle cx={n.x} cy={n.y} r="30" fill="none" stroke="#22D3EE" strokeWidth="6" strokeOpacity="0.06" />
            <text x={n.x} y={n.y + 5} textAnchor="middle" fontSize="15" fontFamily="JetBrains Mono, monospace" fontWeight="700" fill="#22D3EE">{nodeLoad[key]}</text>
            <text x={n.x} y={n.y + 46} textAnchor="middle" fontSize="11" fontFamily="Chivo, sans-serif" fontWeight="700" fill={theme === "light" ? "#334155" : "#CBD5E1"}>{n.label}</text>
          </g>
        ))}
      </svg>

      <div className="mt-2 pt-3 border-t border-white/8">
        <div className="text-[11px] uppercase tracking-[0.18em] text-amber-400/80 font-mono mb-1.5">Uncounted destinations — OUT OF SUMMARY</div>
        <div className="flex flex-wrap gap-2">
          {uncounted.map((u) => (
            <span key={u.destination} className="inline-flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-2.5 py-1 text-xs">
              <span className="text-amber-200/90">{u.destination}</span>
              <span className="font-mono text-amber-400 tnum">{u.open}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
